import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EmailIcon from "@material-ui/icons/Email";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ContactSupportIcon from "@material-ui/icons/ContactSupport";
import GetAppIcon from "@material-ui/icons/GetApp";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";

import { STORE_LINK } from "../../../utils/generateGameCodePdf";

const UserPaymentSuccessRedirectView = (props) => {
  const { t } = useTranslation();
  const { classes, state, handleDownloadPdf } = props;

  if (state.isLoading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.container}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (state.hasError) {
    return (
      <Box className={classes.root}>
        <Box className={classes.container}>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Typography variant="h4" className={classes.title}>
            {t("payment_success.error_title")}
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            {t("payment_success.error_text")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.container}>
        {state.iconUrl ? (
          <img
            src={state.iconUrl}
            alt={state.tourTitle}
            className={classes.tourIcon}
          />
        ) : (
          <CheckCircleIcon className={classes.icon} />
        )}
        <Typography variant="h4" className={classes.title}>
          {t("payment_success.title")}
        </Typography>
        <Typography variant="subtitle1" className={classes.subtitle}>
          {state.tourTitle
            ? t("payment_success.subtitle_with_tour", {
                tourTitle: state.tourTitle,
              })
            : t("payment_success.subtitle")}
        </Typography>

        {state.code ? (
          <Box className={classes.codeBox}>
            <Typography className={classes.codeLabel}>
              {t("payment_success.your_code")}
            </Typography>
            <Typography className={classes.codeValue}>{state.code}</Typography>
          </Box>
        ) : state.isWaitingForCode ? (
          <Box className={classes.codePendingBox}>
            <CircularProgress size={22} />
            <Typography className={classes.codePendingText}>
              {t("payment_success.code_generating")}
            </Typography>
          </Box>
        ) : (
          <Box className={classes.codePendingBox}>
            <Typography className={classes.codePendingText}>
              {t("payment_success.code_timeout")}
            </Typography>
          </Box>
        )}

        <Box className={classes.downloadSection}>
          {state.code && (
            <>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={handleDownloadPdf}
                disabled={state.isGeneratingPdf}
                startIcon={
                  state.isGeneratingPdf ? null : (
                    <GetAppIcon style={{ fontSize: 20 }} />
                  )
                }
              >
                {state.isGeneratingPdf ? (
                  <CircularProgress size={20} style={{ color: "white" }} />
                ) : (
                  t("payment_success.download_pdf")
                )}
              </Button>
              {state.pdfError && (
                <Typography className={classes.pdfError}>
                  {t("payment_success.pdf_error")}
                </Typography>
              )}
            </>
          )}

          {/* Utile même pendant la génération du code : l'acheteur peut
              installer l'application en attendant. */}
          <Button
            variant="outlined"
            className={classes.storeButton}
            href={STORE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<PhoneIphoneIcon style={{ fontSize: 20 }} />}
          >
            {t("payment_success.download_app")}
          </Button>
        </Box>

        <Box className={classes.infoBox}>
          <List className={classes.list}>
            <ListItem className={classes.listItem}>
              <ListItemIcon className={classes.listItemIcon}>
                <EmailIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                className={classes.listItemText}
                primary={
                  state.buyerEmail
                    ? t("payment_success.code_sent_to", {
                        email: state.buyerEmail,
                      })
                    : t("payment_success.email_steps.1")
                }
              />
            </ListItem>
            <ListItem className={classes.listItem}>
              <ListItemIcon className={classes.listItemIcon}>
                <WarningIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={t("payment_success.email_steps.2")}
                className={classes.listItemText}
              />
            </ListItem>
          </List>
        </Box>

        {/* customText est déjà assaini par le back (allowlist de balises) :
            il doit être rendu tel quel, sans ré-échappement. */}
        {state.customText && (
          <Box
            className={classes.customText}
            dangerouslySetInnerHTML={{ __html: state.customText }}
          />
        )}

        {state.contactEmail && (
          <Box className={classes.contactBox}>
            <ContactSupportIcon className={classes.contactIcon} />
            <Box>
              <Typography className={classes.contactLabel}>
                {t("payment_success.contact_organizer")}
              </Typography>
              <a
                href={`mailto:${state.contactEmail}`}
                className={classes.contactLink}
              >
                {state.contactEmail}
              </a>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserPaymentSuccessRedirectView;
